import { authHelpers } from '@/lib/supabase';

const GEMINI_PROXY_PATH = '/.netlify/functions/gemini-proxy';

interface GeminiProxyResponse {
  text?: string;
  error?: string;
}

export type GeminiConnectionStatus = {
  ok: boolean;
  reason?: 'local_netlify_dev_required' | 'unreachable';
};

function requiresNetlifyDevLocally(): boolean {
  if (!import.meta.env.DEV || typeof window === 'undefined') return false;
  const hostname = window.location.hostname;
  const port = window.location.port;
  return (hostname === 'localhost' || hostname === '127.0.0.1') && port !== '8888';
}

export const getGeminiConnectionStatus = async (): Promise<GeminiConnectionStatus> => {
  if (requiresNetlifyDevLocally()) {
    console.info('[ai] Gemini remoto deshabilitado en Vite localhost: usa `netlify dev` para exponer /.netlify/functions/gemini-proxy');
    return { ok: false, reason: 'local_netlify_dev_required' };
  }

  try {
    const response = await fetch(`${GEMINI_PROXY_PATH}?health=1`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });
    return { ok: response.ok, reason: response.ok ? undefined : 'unreachable' };
  } catch {
    return { ok: false, reason: 'unreachable' };
  }
};

export const checkGeminiConnection = async (): Promise<boolean> => {
  const status = await getGeminiConnectionStatus();
  return status.ok;
};

export const sendPromptToGemini = async (prompt: string): Promise<string> => {
  if (!prompt.trim()) {
    throw new Error('El prompt está vacío.');
  }

  const sessionResult = await authHelpers.getSession();
  const accessToken = sessionResult.data?.session?.access_token;
  if (!accessToken) {
    throw new Error('Necesitas iniciar sesión para usar el asistente IA.');
  }

  const response = await fetch(GEMINI_PROXY_PATH, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({ prompt }),
  });

  let data: GeminiProxyResponse = {};
  try {
    data = (await response.json()) as GeminiProxyResponse;
  } catch {
    // Si no hay JSON, se gestiona abajo con mensaje genérico.
  }

  if (!response.ok) {
    throw new Error(data.error || `Gemini proxy error (${response.status})`);
  }

  if (!data.text) {
    throw new Error('Respuesta vacía desde Gemini proxy.');
  }

  return data.text;
};
